import {
  Control,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledSelect from '../../styled/StyledSelect';
import StyledDropZone from '../../styled/StyledDropZone';
import HorizontalLine from '../../ui/HorizontalLine';
import RoomCategoryTypes from './RoomCategoryTypes';
import { useState } from 'react';
import {
  HOTEL_ROOM_CATEGORIES,
  HOTEL_ROOM_CATEGORIES_TRANSLATED,
  HOTEL_ROOM_COMPLEMENTARY_INFORMATION,
  HOTEL_ROOM_COMPLEMENTARY_INFORMATION_TRANSLATED,
} from '@/constants/hotelRoomCategories';
import HotelRoomTarifList from './HotelRoomTarifList';
import HotelOtherPensionTypesPrices from '../HotelCommonOtherPensionTypesPrices';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '@/components/styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '@/components/styled/StyledTranslatableTextarea';
import TranslateIcon from '@/components/translation/TranslateIcon';
import { useTranslateFields } from '@/hooks/useTranslate';

interface HotelRoomCategoryFormProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const HotelRoomCategoryForm = ({
  control,
  index,
  language,
  isViewMode = false,
}: HotelRoomCategoryFormProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<HotelFormDataWithTranslations>();
  const getTranslatedConst = useTranslatedConst();
  const [hotelRoomCategories, setHotelRoomCategories] = useState(
    HOTEL_ROOM_CATEGORIES.map((category) => ({
      label: getTranslatedConst(HOTEL_ROOM_CATEGORIES_TRANSLATED)[category],
      value: category,
    })),
  );
  const [
    hotelRoomComplementaryInformation,
    setHotelRoomComplementaryInformation,
  ] = useState(
    HOTEL_ROOM_COMPLEMENTARY_INFORMATION.map((info) => ({
      label: getTranslatedConst(
        HOTEL_ROOM_COMPLEMENTARY_INFORMATION_TRANSLATED,
      )[info],
      value: info,
    })),
  );

  const hotelRoomCategory = useWatch({
    control,
    name: `hotelRoomCategories.${index}.category`,
  });

  const pricingPensionType = useWatch({
    control,
    name: `pricingPensionType`,
  });

  const shouldFillOtherPensionTypes = useWatch({
    control,
    name: `shouldFillOtherPensionTypes`,
  });

  const areOtherPensionPricesCommon = useWatch({
    control,
    name: `areOtherPensionPricesCommon`,
  });

  const {
    field: { onChange: onChangeRoomCategoryName },
  } = useController({ control, name: `hotelRoomCategories.${index}.name` });

  const {
    field: { onChange: onChangeHotelRoomCategory },
  } = useController({
    control,
    name: `hotelRoomCategories.${index}.category`,
  });

  const {
    field: { onChange: onChangeHotelRoomInfo },
  } = useController({
    control,
    name: `hotelRoomCategories.${index}.complementaryInformation`,
  });

  const { translateFields, isTranslating } = useTranslateFields({
    control: control,
    setValue,
    fields: [
      `hotelRoomCategories.${index}.name`,
      `hotelRoomCategories.${index}.shortDescription`,
      `hotelRoomCategories.${index}.longDescription`,
    ],
    targetLanguage: language,
  });

  return (
    <>
      <div className="flex gap-2 items-center">
        <h2 className="text-base font-medium text-black">
          {t('hotels.roomCategory.informations', { index: index + 1 })}
        </h2>
        <TranslateIcon
          translateFields={() => translateFields({})}
          isTranslating={isTranslating}
          language={language}
          hidden={isViewMode}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <StyledSelect
          options={hotelRoomCategories}
          control={control}
          name={`hotelRoomCategories.${index}.category`}
          title={t('hotels.roomCategory.category')}
          placeholder={t('hotels.roomCategory.selectCategory')}
          creatable
          onCreateOption={(option) => {
            setHotelRoomCategories([
              ...hotelRoomCategories,
              { label: option, value: option },
            ]);
          }}
          onChange={(newValue) => {
            const value = (newValue as { value: string }).value;
            onChangeHotelRoomCategory(newValue);
            onChangeRoomCategoryName(value);
          }}
          disabled={isViewMode}
        />
        <StyledSelect
          options={hotelRoomComplementaryInformation}
          control={control}
          name={`hotelRoomCategories.${index}.complementaryInformation`}
          title={t('hotels.roomCategory.complementaryInformation')}
          placeholder={t('hotels.roomCategory.selectComplementaryInformation')}
          creatable
          onCreateOption={(option) => {
            setHotelRoomComplementaryInformation([
              ...hotelRoomComplementaryInformation,
              { label: option, value: option },
            ]);
          }}
          onChange={(newValue) => {
            const value = (newValue as { value: string }).value;
            onChangeHotelRoomInfo(newValue);
            onChangeRoomCategoryName(
              `${hotelRoomCategory?.value ?? ''} ${value}`,
            );
          }}
          disabled={isViewMode}
        />
      </div>
      <StyledTranslatableInput
        originalName={`hotelRoomCategories.${index}.name`}
        control={control}
        name={`hotelRoomCategories.${index}.name`}
        setValue={setValue}
        language={language}
        title={t('hotels.roomCategory.name')}
        placeholder={t('hotels.roomCategory.namePlaceholder')}
        disabled={isViewMode}
      />
      <StyledDropZone
        control={control}
        name={`hotelRoomCategories.${index}.mainImage`}
        title={t('hotels.mainImage')}
        placeholder={t('hotels.mainImagePlaceholder')}
        disabled={isViewMode}
      />
      <StyledDropZone
        control={control}
        name={`hotelRoomCategories.${index}.images`}
        title={t('hotels.diaporama')}
        placeholder={t('hotels.diaporamaPlaceholder')}
        multiple
        disabled={isViewMode}
      />
      <StyledTranslatableInput
        originalName={`hotelRoomCategories.${index}.shortDescription`}
        control={control}
        name={`hotelRoomCategories.${index}.shortDescription`}
        setValue={setValue}
        language={language}
        title={t('hotels.shortDescription')}
        placeholder={t('hotels.shortDescriptionBodyPlaceholder')}
        disabled={isViewMode}
      />
      <StyledTranslatableTextarea
        originalName={`hotelRoomCategories.${index}.longDescription`}
        control={control}
        name={`hotelRoomCategories.${index}.longDescription`}
        setValue={setValue}
        language={language}
        title={t('hotels.longDescription')}
        placeholder={t('hotels.longDescriptionBodyPlaceholder')}
        rows={3}
        disabled={isViewMode}
      />
      <HorizontalLine />
      {pricingPensionType?.value !== 'AllInclusive' &&
        shouldFillOtherPensionTypes === 'true' &&
        areOtherPensionPricesCommon === 'false' && (
          <>
            <h2 className="text-base font-medium text-black">
              {t('hotels.roomCategory.otherPensionsPrices', {
                index: index + 1,
              })}
            </h2>
            <HotelOtherPensionTypesPrices
              control={control}
              isViewMode={isViewMode}
              categoryIndex={index}
            />
            <HorizontalLine />
          </>
        )}
      <RoomCategoryTypes
        control={control}
        index={index}
        isViewMode={isViewMode}
      />
      <HorizontalLine />
      <HotelRoomTarifList
        control={control}
        index={index}
        isViewMode={isViewMode}
      />
    </>
  );
};

export default HotelRoomCategoryForm;
